import { useState, useEffect, CSSProperties } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

interface Account { id: string; name: string; }

interface Lead {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  source?: string;
  campaignName?: string;
  status?: string;
  createdAt?: string;
}

interface MetaInsight {
  campaign_id:   string;
  campaign_name: string;
  impressions:   string;
  reach:         string;
  clicks:        string;
  spend:         string;
  leads:         string;
}

interface AudienceSegment {
  segmentId:             string;
  segmentName:           string;
  description:           string;
  status:                'pending_setup' | 'active' | 'paused' | 'cancelled';
  activatedAt:           string | null;
  lastBilledAt:          string | null;
  lastSpendAmount:       string | null;
  lastChargeAmount:      string | null;
  stripePaymentMethodId: string | null;
}

interface PortalData {
  clientId:       string;
  clientName:     string;
  clientType:     'b2b' | 'b2c' | null;
  balanceDue:     string | null;
  billingStatus:  string | null;
  autopayEnabled: boolean;
  pixelInstalled: boolean;
  totalEvents:    number;
  recentEvents:   number;
  eventCounts:    Record<string, number>;
  audiences: {
    name:         string;
    memberCount:  number;
    status:       string;
    lastSyncedAt: string | null;
  }[];
  accounts:       Account[];
  leads:          Lead[];
  leadsBySource:  Record<string, number>;
  creditBalance?: number;
  metaInsights:   MetaInsight[] | null;
  segments:       AudienceSegment[];
}

type PortalTab = 'overview' | 'leads' | 'billing' | 'marketplace' | 'settings';

interface LiveCreditPack { size: string; credits: number; priceId: string; amountDollars: number; perCredit: number; popular: boolean; savingsPct: number | null; }
interface Solution { id: string; priceId: string | null; name: string; desc: string; price: number; tag: string; recurring?: boolean; isSetupFee?: boolean; billingInterval?: string | null; billingIntervalCount?: number; billedInArrears?: boolean; trialDays?: number | null; }

const STATUS_COLORS: Record<string, string> = {
  live:     '#34d399', ready:    '#60a5fa', syncing:  '#fbbf24',
  building: '#a78bfa', paused:   '#64748b', error:    '#f87171', unknown: '#334155',
};

const SOURCE_ICONS: Record<string, string> = {
  meta: '🟦', facebook: '🟦', instagram: '🟪', google: '🔵', linkedin: '🔷',
  tiktok: '⬛', twitter: '✖️', x: '✖️', 'meta-lead-ad': '🟦',
  'website-form': '🌐', 'landing-page': '🌐', form: '📋', manual: '📥',
};

const SOURCE_LABELS: Record<string, string> = {
  'meta-lead-ad': 'Meta Ad', 'website-form': 'Website Form',
  'landing-page': 'Landing Page', manual: 'Manual', referral: 'Referral',
};

const inp: CSSProperties = {
  background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42',
  borderRadius: 7, padding: '9px 12px', fontSize: 13,
  fontFamily: 'Sora, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box' as const,
};

// ── Stripe embedded payment form for segment autopay ──────────────────────────
function SegmentPaymentForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [elemReady,  setElemReady]  = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !elemReady) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: stripeErr } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (stripeErr) {
        setError(stripeErr.message ?? 'Payment setup failed');
        setSubmitting(false);
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PaymentElement onReady={() => setElemReady(true)} />
      {error && <div style={{ color: '#f87171', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={submitting}
          style={{ flex: 1, padding: '10px 0', background: 'transparent', color: '#8a9bb5', border: '1px solid #1e2d42', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
          Cancel
        </button>
        <button type="submit" disabled={submitting || !stripe || !elemReady}
          style={{ flex: 2, padding: '10px 0', background: (submitting || !elemReady) ? '#1e2d42' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {submitting ? 'Saving card…' : !elemReady ? 'Loading…' : 'Activate Autopay'}
        </button>
      </div>
    </form>
  );
}

export default function ClientPortal({ onSignOut }: { onSignOut: () => void }) {
  const [data,       setData]       = useState<PortalData | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [errMsg,     setErrMsg]     = useState('');
  const [activeId,   setActiveId]   = useState<string | null>(null);
  const [tab,        setTab]        = useState<PortalTab>('overview');
  const [leadSearch, setLeadSearch] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState<string | null>(null);
  const [cart,        setCart]       = useState<Record<string, number>>({});
  const [cartLoading, setCartLoading] = useState(false);
  const [solutions,   setSolutions]  = useState<Solution[]>([]);
  const [solLoading,  setSolLoading] = useState(false);
  const [livePacks,   setLivePacks]  = useState<LiveCreditPack[]>([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [autopayLoad,   setAutopayLoad]   = useState(false);
  const [promoCode,     setPromoCode]     = useState('');
  const [tosChecked,    setTosChecked]    = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone,    setCancelDone]    = useState<string | null>(null);
  const [diyDeactivating, setDiyDeactivating] = useState(false);
  const [diyDeactivateConfirm, setDiyDeactivateConfirm] = useState(false);
  const [diyDeactivateDone, setDiyDeactivateDone] = useState<{ logId: string; deactivatedAt: string; billingPeriodEnd?: string } | null>(null);

  // Audience segment autopay state
  const [activatingSegId,  setActivatingSegId]  = useState<string | null>(null);
  const [segSetupData,     setSegSetupData]      = useState<{ clientSecret: string; publishableKey: string; segmentId: string; stripePromise: ReturnType<typeof loadStripe> } | null>(null);
  const [segSetupError,    setSegSetupError]     = useState<string | null>(null);
  const [segActivated,     setSegActivated]      = useState<Set<string>>(new Set());

  async function handleActivateSegment(segmentId: string) {
    if (!data) return;
    setActivatingSegId(segmentId);
    setSegSetupError(null);
    try {
      const res = await apiPost<{ clientSecret: string; publishableKey: string }>(
        '/segment/setup-intent', { segmentId, clientId: data.clientId }
      );
      setSegSetupData({ ...res, segmentId, stripePromise: loadStripe(res.publishableKey) });
    } catch (e: any) {
      setSegSetupError(e.message);
      setActivatingSegId(null);
    }
  }

  async function handleSetupAutopay() {
    if (!data) return;
    setAutopayLoad(true);
    try {
      const res = await apiPost<{ url: string }>('/billing/setup-autopay', {
        clientId:   data.clientId,
        clientName: data.clientName,
      });
      window.location.href = res.url;
    } catch (e: any) {
      alert('Failed to start autopay setup: ' + (e as Error).message);
      setAutopayLoad(false);
    }
  }

  async function handleCancelSubscription() {
    if (!data) return;
    if (!window.confirm('Cancel your subscription? It will remain active until the end of the current billing period.')) return;
    setCancelLoading(true);
    try {
      const res = await apiPost<{ cancelled: number; periodEnd: string | null }>('/billing/cancel-subscription', { clientId: data.clientId });
      setCancelDone(res.periodEnd ? `Cancelled — active until ${res.periodEnd}` : 'Cancellation requested. You\'ll keep access through the end of your billing period.');
    } catch (e: any) {
      alert('Cancellation failed: ' + (e as Error).message);
    } finally {
      setCancelLoading(false);
    }
  }

  function fetchPortal(clientId?: string) {
    setLoading(true);
    setErrMsg('');
    const path = clientId ? `/portal?clientId=${clientId}` : '/portal';
    apiGet<PortalData>(path)
      .then(d => { setData(d); setActiveId(d.clientId); })
      .catch(e => setErrMsg(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchPortal(); }, []);

  useEffect(() => {
    if (tab !== 'marketplace') return;
    if (solutions.length === 0 && !solLoading) {
      setSolLoading(true);
      apiGet<Solution[]>('/marketplace/products')
        .then(setSolutions)
        .catch(() => {})
        .finally(() => setSolLoading(false));
    }
    if (livePacks.length === 0 && !packsLoading) {
      setPacksLoading(true);
      apiGet<LiveCreditPack[]>('/marketplace/credits')
        .then(setLivePacks)
        .catch(() => {})
        .finally(() => setPacksLoading(false));
    }
  }, [tab]);

  async function handleBuyPack(pack: string) {
    if (!data) return;
    setBuyLoading(pack);
    try {
      const res = await apiPost<{ url: string }>('/credits/checkout', { pack, clientId: data.clientId });
      window.location.href = res.url;
    } catch (e: any) {
      alert('Checkout failed: ' + e.message);
      setBuyLoading(null);
    }
  }

  async function handleCartCheckout() {
    if (!data) return;
    const items = Object.entries(cart)
      .map(([key, qty]) => {
        const sol = solutions.find(s => s.id === key);
        if (!sol) return null;
        return { name: sol.name, amountDollars: sol.price, priceId: sol.priceId, recurring: sol.recurring ?? false, isSetupFee: sol.isSetupFee ?? false, quantity: qty };
      })
      .filter(Boolean) as { name: string; amountDollars: number; priceId: string | null; recurring: boolean; isSetupFee: boolean; quantity: number }[];
    if (!items.length) return;
    const hasRecurring = items.some(i => i.recurring);
    const hasOneTime   = items.some(i => !i.recurring);
    const allOneTimeAreSetupFees = items.filter(i => !i.recurring).every(i => i.isSetupFee);
    if (hasRecurring && hasOneTime && !allOneTimeAreSetupFees) {
      alert('Subscription plans and one-time items must be purchased separately. Please remove one type from your cart and checkout again.');
      return;
    }
    if (hasRecurring && !tosChecked) {
      alert('Please agree to the billing authorization before continuing.');
      return;
    }
    const recurringItem = items.find(i => i.recurring);
    const totalStr = `$${items.reduce((s, i) => s + i.amountDollars * i.quantity, 0).toLocaleString()}`;
    const acceptance = hasRecurring ? {
      termsVersion: '2026-05-29',
      checkboxText: 'I agree to the Terms of Service and authorize IntentFlo to automatically charge my saved payment method for subscription fees and usage-based charges until I cancel.',
      planName:     recurringItem?.name ?? '',
      priceShown:   totalStr,
      userAgent:    navigator.userAgent,
    } : undefined;
    setCartLoading(true);
    try {
      const res = await apiPost<{ url: string }>('/marketplace/checkout', { clientId: data.clientId, items, promoCode: promoCode.trim() || undefined, acceptance });
      window.location.href = res.url;
    } catch (e: any) {
      alert('Checkout failed: ' + (e as Error).message);
      setCartLoading(false);
    }
  }

  async function handlePayNow() {
    if (!data) return;
    setPayLoading(true);
    try {
      const res = await apiPost<{ url: string }>('/billing/checkout', {
        clientId: data.clientId,
        amountDollars: Number(data.balanceDue),
        description: 'IntentFlo — Balance Due',
      });
      window.location.href = res.url;
    } catch { setPayLoading(false); }
  }

  function exportCSV() {
    const cols = ['Name','Email','Company','Phone','Source','Campaign','Status','Date'];
    const rows = (data!.leads ?? []).map(l => [
      l.name, l.email, l.company ?? '', l.phone ?? '',
      l.source ?? '', l.campaignName ?? '', l.status ?? '',
      l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '',
    ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(','));
    const csv  = [cols.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `${data!.clientName.replace(/\s+/g,'_')}_leads.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  const accounts  = data?.accounts ?? [];
  const leadCount = data?.leads?.length ?? 0;

  const NAV: { id: PortalTab; icon: string; label: string; badge?: number }[] = [
    { id: 'overview',    icon: '⌂',  label: 'Home'        },
    { id: 'leads',       icon: '📋', label: 'Leads', badge: leadCount > 0 ? leadCount : undefined },
    { id: 'billing',     icon: '💳', label: 'Billing'     },
    { id: 'marketplace', icon: '🛒', label: 'Marketplace' },
    { id: 'settings',    icon: '⚙',  label: 'Settings'    },
  ];

  const shell: CSSProperties = {
    display: 'flex', height: '100vh', overflow: 'hidden',
    background: '#060a10', color: '#e8edf5', fontFamily: 'Sora, sans-serif',
  };

  if (loading) return (
    <div style={{ ...shell, alignItems: 'center', justifyContent: 'center' }}>
      <svg width="28" height="28" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
        <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.1)" strokeWidth="2"/>
        <path d="M8 2a6 6 0 0 1 6 6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (errMsg || !data) return (
    <div style={{ ...shell, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 32 }}>⚠️</div>
      <div style={{ color: '#fca5a5', fontSize: 14 }}>{errMsg || 'Could not load your account.'}</div>
      {(errMsg.includes('404') || errMsg.includes('No client')) && (
        <div style={{ fontSize: 12, color: '#64748b' }}>Your account has not been linked yet. Contact your IntentFlo account manager.</div>
      )}
      <button onClick={onSignOut} style={{ marginTop: 8, background: 'none', border: '1px solid #1a2840', borderRadius: 7, color: '#64748b', padding: '7px 18px', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
    </div>
  );

  const formSubmits       = data.eventCounts['FormSubmit'] ?? 0;
  const pageViews         = data.eventCounts['PageView']   ?? 0;
  const totalAudienceSize = data.audiences.reduce((s, a) => s + a.memberCount, 0);
  const filteredLeads     = (data.leads ?? []).filter(l => {
    if (!leadSearch) return true;
    const q = leadSearch.toLowerCase();
    return (l.name ?? '').toLowerCase().includes(q) || (l.email ?? '').toLowerCase().includes(q) || (l.company ?? '').toLowerCase().includes(q);
  });
  const srcLabel = (s: string) => SOURCE_LABELS[s] ?? s;

  const balanceDue  = Number(data.balanceDue ?? 0);
  const isPastDue   = (data.billingStatus === 'past_due' || data.billingStatus === 'outstanding') && balanceDue > 0;
  const isPaid      = data.billingStatus === 'paid' || data.billingStatus === 'current' || balanceDue === 0;
  const billAccent  = isPastDue ? '#f87171' : isPaid ? '#34d399' : '#fbbf24';
  const billingLabel = balanceDue === 0 ? 'Paid'
    : data.billingStatus === 'past_due' ? 'Past Due'
    : data.billingStatus === 'outstanding' ? 'Outstanding'
    : data.billingStatus === 'paid' ? 'Paid'
    : data.billingStatus === 'current' ? 'Current'
    : (data.billingStatus ?? 'Unknown');

  return (
    <div style={shell} className="portal-shell">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity:0;transform:translateY(8px) } to { opacity:1;transform:translateY(0) } }
        .portal-anim { animation: fadeIn 0.2s ease }
        .portal-bottom-nav { display: none; }
        @media (max-width: 768px) {
          .portal-sidebar { display: none !important; }
          .portal-bottom-nav {
            display: flex !important; position: fixed; bottom: 0; left: 0; right: 0;
            background: #080c14; border-top: 1px solid #1a2840;
            z-index: 200; height: 58px; align-items: center; justify-content: space-around; padding: 0 4px;
          }
          .portal-bnav-btn {
            display: flex; flex-direction: column; align-items: center; gap: 2px;
            flex: 1; padding: 6px 2px; background: none; border: none; cursor: pointer;
            font-family: Sora, sans-serif; font-size: 9px; letter-spacing: 0.04em; text-transform: uppercase;
          }
          .portal-bnav-btn.active { color: #60a5fa !important; }
          .portal-main { padding-bottom: 72px !important; }
          .portal-main-pad { padding: 16px !important; }
          .portal-topbar { padding: 0 16px !important; }
          .portal-topbar-date { display: none !important; }
          .portal-billing-amount { font-size: 36px !important; }
          .portal-billing-cta { width: 100% !important; }
          .portal-billing-cta button { width: 100% !important; }
          .portal-mkt-layout { grid-template-columns: 1fr !important; }
          .portal-mkt-balance { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .portal-audience-row { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .portal-leads-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .portal-lead-card { flex-direction: column !important; align-items: flex-start !important; }
          .portal-lead-actions { flex-wrap: wrap !important; }
        }
      `}</style>

      {/* Sidebar */}
      <aside className="portal-sidebar" style={{
        width: 220, flexShrink: 0, background: '#080c14',
        borderRight: '1px solid #111927', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '22px 20px 18px', borderBottom: '1px solid #111927' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32,
              background: 'linear-gradient(150deg,#0d2050,#091428)',
              borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 20px rgba(59,130,246,0.25)',
            }}>
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="3" width="5" height="26" fill="white"/>
                <rect x="4" y="3" width="20" height="5" fill="white"/>
                <rect x="4" y="13" width="14" height="4" fill="white"/>
                <path d="M21 3 L25 3 L18 14 L23 14 L13 29 L10 29 L16 16 L11 16 Z" fill="#3b82f6"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em', color: '#e8edf5', lineHeight: 1.2 }}>
                intent<span style={{ color: '#3b82f6' }}>flo</span>
              </div>
              <div style={{ fontSize: 10, color: '#3b4f6a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Client Portal
              </div>
            </div>
          </div>
          {accounts.length > 1 && (
            <select value={activeId ?? ''} onChange={e => fetchPortal(e.target.value)}
              style={{ ...inp, marginTop: 12, fontSize: 12 }}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
        </div>

        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(n => {
            const active = tab === n.id;
            return (
              <button key={n.id} onClick={() => setTab(n.id)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, padding: '9px 12px', borderRadius: 8,
                background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
                border:     active ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
                color:      active ? '#60a5fa' : '#4a5d7a',
                cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                fontFamily: 'Sora, sans-serif', textAlign: 'left', width: '100%', transition: 'all 0.12s',
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 18, textAlign: 'center', fontSize: 15 }}>{n.icon}</span>
                  {n.label}
                </span>
                {n.badge != null && (
                  <span style={{
                    background: '#3b82f620', color: '#3b82f6', border: '1px solid #3b82f630',
                    borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600,
                  }}>{n.badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div style={{ padding: '14px 16px', borderTop: '1px solid #111927' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#e8edf5', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.clientName}
          </div>
          {data.clientType && (
            <div style={{ fontSize: 10, color: '#3b82f6', marginBottom: 10, letterSpacing: '0.06em' }}>
              {data.clientType === 'b2b' ? 'B2B CLIENT' : 'B2C CLIENT'}
            </div>
          )}
          <button onClick={onSignOut} style={{
            width: '100%', background: 'none', border: '1px solid #1a2840',
            borderRadius: 7, padding: '7px 0', color: '#4a5d7a',
            fontSize: 12, cursor: 'pointer', fontFamily: 'Sora, sans-serif',
          }}>Sign out</button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav className="portal-bottom-nav">
        {NAV.map(n => {
          const active = tab === n.id;
          return (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`portal-bnav-btn${active ? ' active' : ''}`}
              style={{ color: active ? '#60a5fa' : '#4a5d7a' }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>{n.icon}</span>
              {n.label}
              {n.badge != null && (
                <span style={{ background: '#3b82f620', color: '#3b82f6', borderRadius: 8, padding: '0 5px', fontSize: 9, fontWeight: 700 }}>
                  {n.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Main */}
      <main className="portal-main" style={{ flex: 1, overflow: 'auto', background: '#080c14' }}>
        <div className="portal-topbar" style={{
          height: 52, padding: '0 32px', borderBottom: '1px solid #111927',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#080c14', position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#8a9bb5' }}>
            {NAV.find(n => n.id === tab)?.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {data.clientType === 'b2b' && (
              <div style={{
                background: '#0d1f3c', border: '1px solid #1e3a6e',
                borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#60a5fa', fontWeight: 600,
              }}>
                {(data.creditBalance ?? 0).toLocaleString()} credits
              </div>
            )}
            {data.clientType === 'b2c' && balanceDue > 0 && (
              <div style={{
                background: `${billAccent}15`, border: `1px solid ${billAccent}40`,
                borderRadius: 20, padding: '4px 14px', fontSize: 12, color: billAccent, fontWeight: 600,
              }}>
                ${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })} due
              </div>
            )}
            <div className="portal-topbar-date" style={{ fontSize: 11, color: '#1e2d42', fontFamily: 'IBM Plex Mono, monospace' }}>
              {new Date().toISOString().slice(0, 10)}
            </div>
          </div>
        </div>

        <div className="portal-anim portal-main-pad" key={tab} style={{ padding: '32px', maxWidth: 960, margin: '0 auto' }}>

          {/* HOME */}
          {tab === 'overview' && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
                  Welcome back, <span style={{ color: '#3b82f6' }}>{data.clientName}</span>
                </h1>
              </div>

              {/* ── Account balances ─────────────────────────────────── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                {data.clientType === 'b2b' && (
                  <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 12, padding: '20px 22px' }}>
                    <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.07em', marginBottom: 8 }}>CREDIT BALANCE</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: '#60a5fa', lineHeight: 1 }}>{(data.creditBalance ?? 0).toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: '#3b4f6a', marginTop: 6 }}>credits available</div>
                  </div>
                )}
                {data.clientType === 'b2c' && (
                  <div style={{ background: '#0b1220', border: `1px solid ${billAccent}33`, borderRadius: 12, padding: '20px 22px' }}>
                    <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.07em', marginBottom: 8 }}>ACCOUNT BALANCE</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: billAccent, lineHeight: 1 }}>
                      {balanceDue === 0 ? '$0.00' : `$${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                    </div>
                    <div style={{ fontSize: 12, color: '#3b4f6a', marginTop: 6 }}>{billingLabel}</div>
                  </div>
                )}
                <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 12, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.07em', marginBottom: 8 }}>TOTAL LEADS</div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: '#34d399', lineHeight: 1 }}>{leadCount.toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: '#3b4f6a', marginTop: 6 }}>captured</div>
                </div>
                <div style={{ background: '#0b1220', border: `1px solid ${data.pixelInstalled ? 'rgba(52,211,153,0.2)' : '#162035'}`, borderRadius: 12, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.07em', marginBottom: 8 }}>PIXEL STATUS</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: data.pixelInstalled ? '#34d399' : '#334155', boxShadow: data.pixelInstalled ? '0 0 8px rgba(52,211,153,0.6)' : 'none', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: data.pixelInstalled ? '#34d399' : '#64748b' }}>{data.pixelInstalled ? 'Active' : 'Not installed'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#3b4f6a', marginTop: 6 }}>{data.pixelInstalled ? `${data.recentEvents.toLocaleString()} events last 30 days` : 'Contact your account manager'}</div>
                </div>
              </div>

              {/* ── Action items ─────────────────────────────────────── */}
              {(() => {
                const actions: { icon: string; text: string; cta?: string; onClick?: () => void; color: string }[] = [];
                if (isPastDue && balanceDue > 0)
                  actions.push({ icon: '💳', text: `Payment of $${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })} is overdue.`, cta: 'Pay now', onClick: handlePayNow, color: '#f87171' });
                if (!data.autopayEnabled && data.clientType === 'b2c')
                  actions.push({ icon: '🔄', text: 'No card on file for monthly service invoices — set up autopay to avoid missed payments.', cta: 'Set up autopay', onClick: handleSetupAutopay, color: '#fbbf24' });
                (data.segments ?? []).filter(s => s.status === 'pending_setup').forEach(s =>
                  actions.push({ icon: '⚡', text: `Audience segment "${s.segmentName}" needs autopay setup.`, cta: 'Activate', onClick: () => { handleActivateSegment(s.segmentId); setTab('marketplace'); }, color: '#fbbf24' })
                );
                if (actions.length === 0) return (
                  <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                    <span style={{ fontSize: 13, color: '#3b4f6a' }}>No pending items or notifications.</span>
                  </div>
                );
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                    {actions.map((a, i) => (
                      <div key={i} style={{ background: `${a.color}0f`, border: `1px solid ${a.color}33`, borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 16 }}>{a.icon}</span>
                          <span style={{ fontSize: 13, color: a.color }}>{a.text}</span>
                        </div>
                        {a.cta && a.onClick && (
                          <button onClick={a.onClick} style={{ padding: '6px 16px', background: a.color, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{a.cta}</button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>
          )}

          {/* LEADS */}
          {tab === 'leads' && (
            <div>
              <div className="portal-leads-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                  <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Your Leads</h1>
                  <p style={{ color: '#3b4f6a', fontSize: 13, marginTop: 4 }}>People who submitted info through your campaigns</p>
                </div>
                {(data.leads?.length ?? 0) > 0 && (
                  <button onClick={exportCSV} style={{ background: '#10b98115', border: '1px solid #10b98130', borderRadius: 8, padding: '8px 18px', color: '#10b981', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Sora, sans-serif' }}>
                    ↓ Export CSV
                  </button>
                )}
              </div>

              {Object.keys(data.leadsBySource ?? {}).length > 0 && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                  {Object.entries(data.leadsBySource ?? {}).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                    <div key={src} style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 10, padding: '14px 20px', minWidth: 110 }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{SOURCE_ICONS[src.toLowerCase()] ?? '📋'}</div>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>{count}</div>
                      <div style={{ fontSize: 11, color: '#4a5d7a', marginTop: 2 }}>{srcLabel(src)}</div>
                    </div>
                  ))}
                </div>
              )}

              <input placeholder="Search by name, email, company…" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} style={{ ...inp, marginBottom: 16 }} />

              {filteredLeads.length === 0 ? (
                <div style={{ background: '#0b1220', border: '1px dashed #1a2840', borderRadius: 10, padding: 32, textAlign: 'center', color: '#3b4f6a', fontSize: 13 }}>
                  {(data.leads?.length ?? 0) === 0 ? "No leads yet — they'll appear here as they come in." : 'No leads match your search.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredLeads.map(l => (
                    <div key={l.id} className="portal-lead-card" style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0d1f3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#3b82f6', flexShrink: 0 }}>
                          {(l.name ?? '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{l.name}</div>
                          <div style={{ fontSize: 12, color: '#4a5d7a', marginTop: 2 }}>
                            {l.email}{l.company && <span style={{ marginLeft: 8, color: '#334155' }}>· {l.company}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="portal-lead-actions" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        {l.source && <span style={{ fontSize: 11, color: '#4a5d7a' }}>{SOURCE_ICONS[l.source.toLowerCase()] ?? '📋'} {srcLabel(l.source)}</span>}
                        {l.phone  && <a href={`tel:${l.phone}`} style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>{l.phone}</a>}
                        <a href={`mailto:${l.email}`} style={{ background: '#0d1f3c', border: '1px solid #1e3a6e', borderRadius: 7, padding: '5px 12px', fontSize: 12, color: '#60a5fa', textDecoration: 'none' }}>Contact ↗</a>
                        <div style={{ fontSize: 11, color: '#334155' }}>{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* BILLING */}
          {tab === 'billing' && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Billing</h1>
                <p style={{ color: '#3b4f6a', fontSize: 13, marginTop: 4 }}>Manage your account balance and payments</p>
              </div>

              {data.clientType === 'b2b' && (
                <div style={{ background: 'linear-gradient(135deg,#0d1f3c,#091428)', border: '1px solid #1e3a6e', borderRadius: 16, padding: 32, marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600, letterSpacing: '0.1em', marginBottom: 12 }}>B2B CONTACT CREDITS</div>
                      <div className="portal-billing-amount" style={{ fontSize: 56, fontWeight: 800, color: '#60a5fa', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '-0.03em', lineHeight: 1 }}>
                        {(data.creditBalance ?? 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: 13, color: '#3b4f6a', marginTop: 10 }}>
                        Verified contacts remaining · 1 credit = 1 contact · Credits never expire
                      </div>
                      {(data.creditBalance ?? 0) < 500 && (
                        <div style={{ marginTop: 10, display: 'inline-block', background: '#fbbf2420', border: '1px solid #fbbf2440', borderRadius: 7, padding: '4px 12px', fontSize: 12, color: '#fbbf24' }}>
                          ⚠ Running low — consider topping up
                        </div>
                      )}
                    </div>
                    <div className="portal-billing-cta" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <button onClick={() => setTab('marketplace')} style={{
                        background: '#3b82f6', color: '#fff', border: 'none',
                        borderRadius: 10, padding: '14px 28px', fontSize: 15, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'Sora, sans-serif',
                      }}>Buy More Credits →</button>
                      <div style={{ fontSize: 11, color: '#3b4f6a', textAlign: 'center' }}>Packs from $750 · Secure Stripe checkout</div>
                    </div>
                  </div>
                </div>
              )}

              {data.clientType === 'b2c' && (
                <div style={{ background: `${billAccent}08`, border: `1px solid ${billAccent}30`, borderRadius: 16, padding: 32, marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 11, color: billAccent, fontWeight: 600, letterSpacing: '0.1em', marginBottom: 12 }}>ACCOUNT BALANCE</div>
                      <div className="portal-billing-amount" style={{ fontSize: 56, fontWeight: 800, color: billAccent, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '-0.03em', lineHeight: 1 }}>
                        ${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                        <div style={{ background: `${billAccent}22`, color: billAccent, border: `1px solid ${billAccent}44`, borderRadius: 7, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>{billingLabel}</div>
                        <span style={{ fontSize: 12, color: '#3b4f6a' }}>Balance due on your account</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      {balanceDue > 0 ? (
                        <>
                          <button disabled={payLoading} onClick={handlePayNow} style={{
                            background: billAccent, color: '#fff', border: 'none',
                            borderRadius: 10, padding: '14px 28px', fontSize: 15, fontWeight: 700,
                            cursor: payLoading ? 'not-allowed' : 'pointer', opacity: payLoading ? 0.7 : 1,
                            fontFamily: 'Sora, sans-serif',
                          }}>{payLoading ? 'Redirecting…' : 'Pay Now →'}</button>
                          <div style={{ fontSize: 11, color: '#3b4f6a' }}>Secure checkout via Stripe</div>
                        </>
                      ) : (
                        <div style={{ fontSize: 36, color: '#34d399' }}>✓</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Autopay setup */}
              <div style={{
                background: data.autopayEnabled ? 'rgba(52,211,153,0.06)' : '#0b1220',
                border: data.autopayEnabled ? '1px solid rgba(52,211,153,0.25)' : '1px solid #162035',
                borderRadius: 12, padding: '20px 24px', marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: data.autopayEnabled ? '#34d399' : '#8a9bb5', marginBottom: 4 }}>
                      {data.autopayEnabled ? '✓ Auto-Pay Active' : 'Set Up Auto-Pay'}
                    </div>
                    <div style={{ fontSize: 12, color: '#4a5d7a', lineHeight: 1.6 }}>
                      {data.autopayEnabled
                        ? 'Your card is on file — monthly service invoices will be charged automatically. You\'ll get a receipt by email after each charge.'
                        : (<>
                            Save a card on file for your <strong style={{ color: '#8a9bb5' }}>monthly IntentFlo service invoices</strong> (retainers, management fees, etc.).<br />
                            Your card will be charged when IntentFlo issues an invoice — you\'ll get a receipt by email. This does <strong style={{ color: '#8a9bb5' }}>not</strong> cover one-time purchases (credits, TV ads) or audience segment billing — those are separate.
                          </>)}
                    </div>
                  </div>
                  {!data.autopayEnabled && (
                    <button disabled={autopayLoad} onClick={handleSetupAutopay} style={{
                      background: '#3b82f6', color: '#fff', border: 'none',
                      borderRadius: 9, padding: '10px 22px', fontSize: 13, fontWeight: 700,
                      cursor: autopayLoad ? 'not-allowed' : 'pointer', opacity: autopayLoad ? 0.7 : 1,
                      fontFamily: 'Sora, sans-serif', flexShrink: 0,
                    }}>{autopayLoad ? 'Redirecting…' : 'Set Up Auto-Pay →'}</button>
                  )}
                </div>
              </div>

              <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 12, padding: '20px 24px' }}>
                <div style={{ fontSize: 13, color: '#8a9bb5', fontWeight: 600, marginBottom: 6 }}>Questions about your bill?</div>
                <div style={{ fontSize: 13, color: '#4a5d7a' }}>
                  Contact your account manager or email{' '}
                  <a href="mailto:info@intentflo.com" style={{ color: '#3b82f6' }}>info@intentflo.com</a>
                </div>
              </div>
            </div>
          )}

          {/* MARKETPLACE */}
          {tab === 'marketplace' && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Marketplace</h1>
                <p style={{ color: '#3b4f6a', fontSize: 13, marginTop: 4 }}>Select services and add to cart — checkout securely via Stripe</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 24, alignItems: 'start' }} className="portal-mkt-layout">

                {/* Left: Products */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>

                  {/* Marketing Solutions — always visible */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 16 }}>MARKETING SOLUTIONS</div>
                    {solLoading ? (
                      <div style={{ color: '#3b4f6a', fontSize: 13, padding: '24px 0' }}>Loading products…</div>
                    ) : solutions.length === 0 ? (
                      <div style={{ background: '#0b1220', border: '1px dashed #1a2840', borderRadius: 10, padding: '28px 20px', color: '#3b4f6a', fontSize: 13, textAlign: 'center' }}>No products available yet.</div>
                    ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
                      {solutions.filter(s => !s.isSetupFee).map(s => {
                        const qty = cart[s.id] ?? 0;
                        const setupFee = solutions.find(sf => sf.isSetupFee);
                        function addToCart() {
                          setCart(c => {
                            const next: Record<string, number> = { ...c, [s.id]: 1 };
                            // auto-include setup fee when adding a subscription plan
                            if (s.recurring && setupFee && !c[setupFee.id]) {
                              next[setupFee.id] = 1;
                            }
                            return next;
                          });
                        }
                        return (
                          <div key={s.id} style={{
                            background: qty > 0 ? 'linear-gradient(135deg,#0d1f3c,#091428)' : '#0b1220',
                            border: qty > 0 ? '1px solid #3b82f6' : '1px solid #162035',
                            borderRadius: 12, padding: '20px 20px 16px',
                            display: 'flex', flexDirection: 'column', gap: 10, transition: 'all 0.15s',
                          }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#e8edf5', marginBottom: 5 }}>{s.name}</div>
                              {s.desc && <div style={{ fontSize: 12, color: '#4a5d7a', lineHeight: 1.55 }}>{s.desc}</div>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                              <div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#60a5fa' }}>${s.price.toLocaleString()}</div>
                                <div style={{ fontSize: 11, color: '#4a5d7a' }}>{s.tag}</div>
                              </div>
                              {qty === 0 ? (
                                <button onClick={addToCart} style={{
                                  background: '#0d1f3c', border: '1px solid #1e3a6e', borderRadius: 8,
                                  padding: '8px 16px', color: '#60a5fa', fontSize: 13, fontWeight: 600,
                                  cursor: 'pointer', fontFamily: 'Sora, sans-serif',
                                }}>+ Add</button>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <button onClick={() => setCart(c => { const n = { ...c }; if ((n[s.id] ?? 0) <= 1) delete n[s.id]; else n[s.id]--; return n; })} style={{ width: 28, height: 28, background: '#1a2840', border: '1px solid #1e3a6e', borderRadius: 6, color: '#60a5fa', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora, sans-serif' }}>−</button>
                                  <span style={{ fontSize: 14, fontWeight: 700, color: '#e8edf5', minWidth: 16, textAlign: 'center' }}>{qty}</span>
                                  <button onClick={() => setCart(c => ({ ...c, [s.id]: (c[s.id] ?? 0) + 1 }))} style={{ width: 28, height: 28, background: '#3b82f6', border: 'none', borderRadius: 6, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora, sans-serif' }}>+</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>

                  {/* B2B Credit Packs — b2b only */}
                  {data.clientType === 'b2b' && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 16 }}>B2B CONTACT CREDIT PACKS</div>
                      {packsLoading && <div style={{ fontSize: 13, color: '#4a5d7a', marginBottom: 12 }}>Loading prices…</div>}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14 }}>
                        {livePacks.map(p => (
                          <div key={p.size} style={{ background: p.popular ? 'linear-gradient(135deg,#0d1f3c,#091428)' : '#0b1220', border: p.popular ? '1px solid #3b82f6' : '1px solid #162035', borderRadius: 14, padding: '22px 20px', position: 'relative' }}>
                            {p.popular && (
                              <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: '#3b82f6', color: '#fff', borderRadius: 20, padding: '2px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>MOST POPULAR</div>
                            )}
                            <div style={{ fontSize: 26, fontWeight: 800, color: '#e8edf5', marginBottom: 3 }}>{p.credits.toLocaleString()}</div>
                            <div style={{ fontSize: 12, color: '#4a5d7a', marginBottom: 12 }}>B2B contacts</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#60a5fa', marginBottom: 2 }}>${p.amountDollars.toLocaleString()}</div>
                            <div style={{ fontSize: 12, color: '#3b4f6a', marginBottom: 3 }}>${p.perCredit.toFixed(2)} / contact · one-time</div>
                            {p.savingsPct != null
                              ? <div style={{ fontSize: 11, color: '#34d399', fontWeight: 600, marginBottom: 14 }}>Save {p.savingsPct}%</div>
                              : <div style={{ height: 18, marginBottom: 14 }} />
                            }
                            <button disabled={buyLoading === p.size} onClick={() => handleBuyPack(p.size)} style={{
                              width: '100%', padding: '10px 0',
                              background: p.popular ? '#3b82f6' : '#0d1f3c',
                              border: p.popular ? 'none' : '1px solid #1e3a6e',
                              borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700,
                              cursor: buyLoading === p.size ? 'not-allowed' : 'pointer',
                              opacity: buyLoading === p.size ? 0.7 : 1,
                              fontFamily: 'Sora, sans-serif', transition: 'all 0.15s',
                            }}>
                              {buyLoading === p.size ? 'Redirecting…' : 'Buy Now →'}
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 10, padding: '14px 20px', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 12, color: '#4a5d7a' }}>Current credit balance</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#60a5fa' }}>{(data.creditBalance ?? 0).toLocaleString()} credits</div>
                      </div>
                    </div>
                  )}

                  {/* Audience Segments */}
                  {(data.segments?.length ?? 0) > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 16 }}>AUDIENCE SEGMENTS</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {data.segments.map(seg => {
                        const segColors: Record<string, string> = { active: '#34d399', pending_setup: '#fbbf24', paused: '#64748b', cancelled: '#ef4444' };
                        const col = segColors[seg.status] ?? '#4a5d7a';
                        const isSetupOpen = segSetupData?.segmentId === seg.segmentId;
                        const isDone = segActivated.has(seg.segmentId);
                        return (
                          <div key={seg.segmentId} style={{ background: '#0b1220', border: `1px solid ${col}33`, borderRadius: 12, padding: '18px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#e8edf5', marginBottom: 3 }}>{seg.segmentName}</div>
                                {seg.description && <div style={{ fontSize: 12, color: '#4a5d7a', marginBottom: 6 }}>{seg.description}</div>}
                                {seg.status === 'active' && seg.lastBilledAt && (
                                  <div style={{ fontSize: 12, color: '#3b4f6a' }}>
                                    Last billed {new Date(seg.lastBilledAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                    {seg.lastSpendAmount && ` · Meta spend $${parseFloat(seg.lastSpendAmount).toLocaleString()}`}
                                    {seg.lastChargeAmount && ` · Charged $${parseFloat(seg.lastChargeAmount).toLocaleString()}`}
                                  </div>
                                )}
                                {seg.status === 'active' && !seg.lastBilledAt && (
                                  <div style={{ fontSize: 12, color: '#3b4f6a' }}>Autopay active — billed on the 1st of each month at 30% of Meta ad spend</div>
                                )}
                                {seg.status === 'pending_setup' && !isDone && (
                                  <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 2 }}>Set up autopay to activate this segment. You'll be charged 30% of your Meta ad spend, billed monthly. No charge for months with no spend.</div>
                                )}
                                {isDone && (
                                  <div style={{ fontSize: 12, color: '#34d399', marginTop: 2 }}>✓ Card saved! Autopay is activating — no action needed.</div>
                                )}
                                {segSetupError && activatingSegId === seg.segmentId && (
                                  <div style={{ fontSize: 12, color: '#f87171', marginTop: 4 }}>{segSetupError}</div>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                <div style={{ background: `${col}22`, color: col, border: `1px solid ${col}44`, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {isDone ? 'activating' : seg.status.replace('_', ' ')}
                                </div>
                                {seg.status === 'pending_setup' && !isDone && !isSetupOpen && (
                                  <button
                                    onClick={() => handleActivateSegment(seg.segmentId)}
                                    disabled={activatingSegId === seg.segmentId}
                                    style={{ padding: '6px 14px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                  >
                                    {activatingSegId === seg.segmentId ? 'Loading…' : 'Activate Autopay →'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Stripe Elements inline form */}
                            {isSetupOpen && segSetupData && (
                              <div style={{ marginTop: 16, padding: 16, background: '#060a12', borderRadius: 10, border: '1px solid #162035' }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#e8edf5', marginBottom: 12 }}>Add payment method for autopay</div>
                                <Elements
                                  stripe={segSetupData.stripePromise}
                                  options={{ clientSecret: segSetupData.clientSecret, appearance: { theme: 'night', variables: { colorBackground: '#060a12', colorPrimary: '#3b82f6', fontFamily: 'Sora, sans-serif' } } }}
                                >
                                  <SegmentPaymentForm
                                    onSuccess={() => {
                                      setSegActivated(p => new Set([...p, seg.segmentId]));
                                      setSegSetupData(null);
                                      setActivatingSegId(null);
                                    }}
                                    onCancel={() => { setSegSetupData(null); setActivatingSegId(null); }}
                                  />
                                </Elements>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )}

                </div>

                {/* Right: Cart */}
                <div style={{ position: 'sticky', top: 70, alignSelf: 'start' }}>
                  <div style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid #162035', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#e8edf5' }}>🛒 Cart</div>
                      {Object.keys(cart).length > 0 && (
                        <button onClick={() => { setCart({}); setTosChecked(false); }} style={{ background: 'none', border: 'none', color: '#4a5d7a', fontSize: 12, cursor: 'pointer', fontFamily: 'Sora, sans-serif' }}>Clear</button>
                      )}
                    </div>
                    {Object.keys(cart).length === 0 ? (
                      <div style={{ padding: '28px 20px', textAlign: 'center', color: '#3b4f6a', fontSize: 13 }}>
                        Add items to get started
                      </div>
                    ) : (
                      <div>
                        <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto' }}>
                          {Object.entries(cart).map(([key, qty]) => {
                            const sol = solutions.find(s => s.id === key);
                            if (!sol) return null;
                            return (
                              <div key={key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#e8edf5', lineHeight: 1.35 }}>{sol.name}</div>
                                  <div style={{ fontSize: 11, color: '#4a5d7a' }}>
                                    ×{qty} · {sol.tag}
                                    {sol.isSetupFee && <span style={{ marginLeft: 6, color: '#fbbf24', fontSize: 10, fontWeight: 600 }}>AUTO-INCLUDED</span>}
                                  </div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa', flexShrink: 0 }}>${(sol.price * qty).toLocaleString()}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: '1px solid #162035' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input
                              type="text"
                              placeholder="Promo code"
                              value={promoCode}
                              onChange={e => setPromoCode(e.target.value.toUpperCase())}
                              style={{
                                flex: 1, background: '#060a10', color: '#e8edf5',
                                border: '1px solid #1e2d42', borderRadius: 7,
                                padding: '8px 10px', fontSize: 12,
                                fontFamily: 'DM Mono, monospace', outline: 'none',
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #162035' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                            <div style={{ fontSize: 13, color: '#8a9bb5' }}>Total</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#e8edf5' }}>
                              ${Object.entries(cart).reduce((sum, [key, qty]) => {
                                const sol = solutions.find(s => s.id === key);
                                return sum + (sol ? sol.price * qty : 0);
                              }, 0).toLocaleString()}
                            </div>
                          </div>

                          {/* Billing authorization — shown only when cart has subscription */}
                          {Object.keys(cart).some(k => solutions.find(s => s.id === k)?.recurring) && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{
                                background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)',
                                borderRadius: 8, padding: '10px 12px', marginBottom: 10,
                                fontSize: 11, color: '#4a5d7a', lineHeight: 1.6,
                              }}>
                                By subscribing, you authorize IntentFlo to automatically charge your payment method for monthly subscription fees. We'll email you a receipt after each successful payment. If a payment fails, we'll retry automatically and may temporarily suspend service until payment succeeds.
                              </div>
                              <label style={{ display: 'flex', gap: 8, cursor: 'pointer', alignItems: 'flex-start' }}>
                                <input
                                  type="checkbox"
                                  checked={tosChecked}
                                  onChange={e => setTosChecked(e.target.checked)}
                                  style={{ accentColor: '#3b82f6', width: 14, height: 14, marginTop: 2, flexShrink: 0 }}
                                />
                                <span style={{ fontSize: 11, color: '#8a9bb5', lineHeight: 1.5 }}>
                                  I agree to the{' '}
                                  <a href="/terms/" target="_blank" style={{ color: '#3b82f6' }}>Terms of Service</a>
                                  {' '}and <a href="/managed-services/" target="_blank" style={{ color: '#3b82f6' }}>Managed Services Addendum</a>
                                  {' '}and authorize IntentFlo to automatically charge my saved payment method for subscription fees and usage-based charges until I cancel.
                                </span>
                              </label>
                            </div>
                          )}

                          <button
                            disabled={cartLoading || (Object.keys(cart).some(k => solutions.find(s => s.id === k)?.recurring) && !tosChecked)}
                            onClick={handleCartCheckout}
                            style={{
                              width: '100%', padding: '12px 0',
                              background: '#3b82f6', border: 'none', borderRadius: 10,
                              color: '#fff', fontSize: 14, fontWeight: 700,
                              cursor: (cartLoading || (Object.keys(cart).some(k => solutions.find(s => s.id === k)?.recurring) && !tosChecked)) ? 'not-allowed' : 'pointer',
                              opacity: (cartLoading || (Object.keys(cart).some(k => solutions.find(s => s.id === k)?.recurring) && !tosChecked)) ? 0.5 : 1,
                              fontFamily: 'Sora, sans-serif',
                            }}
                          >
                            {cartLoading ? 'Redirecting…' : 'Checkout →'}
                          </button>
                          <div style={{ fontSize: 11, color: '#3b4f6a', textAlign: 'center', marginTop: 8 }}>Secure checkout via Stripe</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* SETTINGS */}
          {tab === 'settings' && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Settings</h1>
                <p style={{ color: '#3b4f6a', fontSize: 13, marginTop: 4 }}>Connect your ad platforms to enable audience syncing</p>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 10 }}>GRANT AD PLATFORM ACCESS</div>
              <p style={{ fontSize: 13, color: '#3b4f6a', marginBottom: 20, lineHeight: 1.6 }}>
                Give IntentFlo access to each platform. Takes less than 2 minutes per platform — click, paste, and confirm.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {PLATFORM_ACCESS_STEPS.map(p => (
                  <div key={p.key} style={{ background: '#0b1220', border: '1px solid #162035', borderRadius: 12, padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <span style={{ fontSize: 22 }}>{p.icon}</span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{p.platform}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#4a5d7a', marginBottom: 8 }}>
                      <span style={{ color: '#3b82f6', fontWeight: 600 }}>Step 1 —</span> Copy our {p.label}:
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#060a10', border: '1px solid #1a2840', borderRadius: 7, padding: '7px 14px', marginBottom: 14, fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#60a5fa' }}>
                      {p.id}
                      <button onClick={() => navigator.clipboard.writeText(p.id)} style={{ background: '#1a2840', border: 'none', cursor: 'pointer', color: '#60a5fa', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'Sora, sans-serif' }}>Copy</button>
                    </div>
                    <div style={{ fontSize: 12, color: '#4a5d7a', marginBottom: 10 }}>
                      <span style={{ color: '#3b82f6', fontWeight: 600 }}>Step 2 —</span> {p.instruction}
                    </div>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: '#0d1f3c', border: '1px solid #1e3a6e', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: '#60a5fa', textDecoration: 'none' }}>
                      Open {p.platform} ↗
                    </a>
                  </div>
                ))}
              </div>

              {/* Subscription cancellation */}
              {data.autopayEnabled && (
                <div style={{ marginTop: 36, borderTop: '1px solid #162035', paddingTop: 28 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 12 }}>SUBSCRIPTION</div>

                  {/* DIY Data notice — always visible */}
                  <div style={{ background: '#0b1220', border: '1px solid #1e3a6e', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#fbbf24', fontWeight: 600, marginBottom: 4 }}>⚠ DIY Data &amp; Delivery — Important Billing Notice</div>
                    <div style={{ fontSize: 12, color: '#4a5d7a', lineHeight: 1.7 }}>
                      DIY Data fees (30% of Ad Spend) are billed monthly <strong style={{ color: '#8a9bb5' }}>in arrears</strong> for campaigns that target IntentFlo Audiences.
                      To stop future DIY charges you must <strong style={{ color: '#e8edf5' }}>(a) remove IntentFlo Audiences from your ad account(s) and ad sets</strong>, and <strong style={{ color: '#e8edf5' }}>(b) deactivate DIY Data below</strong>.
                      You may receive a final invoice for the current billing period based on platform/vendor reporting. Cancelling a fixed monthly plan does <strong style={{ color: '#e8edf5' }}>not</strong> automatically stop DIY fees.
                      {' '}<a href="/help/diy-billing/" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Learn more →</a>
                    </div>
                  </div>

                  {/* Cancel Fixed Monthly Plan */}
                  {cancelDone ? (
                    <div style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '14px 18px', fontSize: 13, color: '#34d399', marginBottom: 16 }}>
                      ✓ {cancelDone}
                    </div>
                  ) : (
                    <div style={{ background: '#0b1220', border: '1px solid #1e2d40', borderRadius: 10, padding: '20px 24px', marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#8a9bb5', marginBottom: 6 }}>Cancel Fixed Monthly Plan</div>
                      <p style={{ fontSize: 12, color: '#4a5d7a', margin: '0 0 4px', lineHeight: 1.6 }}>
                        Cancels your monthly plan at the end of the current billing period. You'll retain access through that date.
                      </p>
                      <p style={{ fontSize: 12, color: '#fbbf24', margin: '0 0 16px', lineHeight: 1.6 }}>
                        This does <strong>not</strong> stop DIY Data &amp; Delivery charges. See above.
                      </p>
                      <button
                        disabled={cancelLoading}
                        onClick={handleCancelSubscription}
                        style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: cancelLoading ? 'not-allowed' : 'pointer', opacity: cancelLoading ? 0.6 : 1 }}
                      >
                        {cancelLoading ? 'Cancelling…' : 'Cancel Fixed Monthly Plan'}
                      </button>
                    </div>
                  )}

                  {/* Deactivate DIY Data */}
                  {(data.segments ?? []).filter(s => s.status === 'active').length > 0 && (
                    <div style={{ background: '#0b1220', border: '1px solid #1e2d40', borderRadius: 10, padding: '20px 24px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#8a9bb5', marginBottom: 6 }}>Deactivate DIY Data &amp; Delivery</div>
                      <p style={{ fontSize: 12, color: '#4a5d7a', margin: '0 0 12px', lineHeight: 1.6 }}>
                        The following audience segments are currently active. Deactivating will stop future billing cycles, but you <strong style={{ color: '#e8edf5' }}>must also remove these audiences from your ad campaigns</strong> in Meta/Google to fully stop charges.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                        {(data.segments ?? []).filter(s => s.status === 'active').map(s => (
                          <div key={s.segmentId} style={{ background: '#060a12', border: '1px solid #162035', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#8a9bb5' }}>
                            <strong style={{ color: '#e8edf5' }}>{s.segmentName}</strong>
                            {s.lastBilledAt && <span style={{ color: '#4a5d7a' }}> · Last billed {new Date(s.lastBilledAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                            <div style={{ fontSize: 11, color: '#3b4f6a', marginTop: 3 }}>Remove this audience from your ad sets in Meta Ads Manager to stop platform-side spend.</div>
                          </div>
                        ))}
                      </div>
                      {diyDeactivateDone ? (
                        <div style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '14px 18px' }}>
                          <div style={{ fontSize: 13, color: '#34d399', marginBottom: 6 }}>✓ DIY Data deactivated.</div>
                          <div style={{ fontSize: 12, color: '#4a5d7a', lineHeight: 1.7 }}>
                            A confirmation email has been sent to your account address. You may receive a final invoice after the billing period closes
                            {diyDeactivateDone.billingPeriodEnd && <> on <strong style={{ color: '#8a9bb5' }}>{diyDeactivateDone.billingPeriodEnd}</strong></>}.<br />
                            <span style={{ color: '#3b4f6a' }}>Audit ID: <span style={{ fontFamily: 'monospace', color: '#8a9bb5', userSelect: 'all' }}>{diyDeactivateDone.logId}</span></span><br />
                            <span style={{ color: '#3b4f6a' }}>Deactivated at: {new Date(diyDeactivateDone.deactivatedAt).toLocaleString()}</span>
                          </div>
                          <button
                            onClick={() => {
                              const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>IntentFlo Deactivation Confirmation</title>
<style>body{font-family:system-ui,sans-serif;max-width:640px;margin:40px auto;color:#111;line-height:1.6}h1{font-size:20px}table{border-collapse:collapse;width:100%}td{padding:8px 12px;border:1px solid #ddd;font-size:13px}th{background:#f4f4f4;text-align:left;padding:8px 12px;border:1px solid #ddd}@media print{button{display:none}}</style>
</head><body>
<h1>IntentFlo — DIY Data Deactivation Confirmation</h1>
<table>
<tr><th>Field</th><th>Value</th></tr>
<tr><td>Audit ID</td><td style="font-family:monospace">${diyDeactivateDone.logId}</td></tr>
<tr><td>Deactivated at</td><td>${new Date(diyDeactivateDone.deactivatedAt).toLocaleString()}</td></tr>
${diyDeactivateDone.billingPeriodEnd ? `<tr><td>Billing period ends</td><td>${diyDeactivateDone.billingPeriodEnd}</td></tr>` : ''}
</table>
<p style="margin-top:24px;font-size:13px;color:#555">Keep this document for your records. If you need to raise a billing dispute, include the Audit ID above and email <a href="mailto:info@intentflo.com">info@intentflo.com</a>.</p>
<button onclick="window.print()" style="margin-top:16px;padding:8px 20px;font-size:13px;cursor:pointer">Print / Save as PDF</button>
</body></html>`;
                              const w = window.open('', '_blank');
                              if (w) { w.document.write(html); w.document.close(); }
                            }}
                            style={{ marginTop: 10, background: 'transparent', border: '1px solid #4a5d7a', color: '#8a9bb5', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
                          >
                            ↓ Download confirmation (PDF)
                          </button>
                        </div>
                      ) : (
                        <>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
                            <input type="checkbox" checked={diyDeactivateConfirm} onChange={e => setDiyDeactivateConfirm(e.target.checked)}
                              style={{ marginTop: 2, accentColor: '#3b82f6', flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: '#4a5d7a', lineHeight: 1.5 }}>
                              I confirm I have removed IntentFlo Audiences from my ad account(s) and understand I may still receive a final invoice for usage in the current billing period.
                            </span>
                          </label>
                          <button
                            disabled={!diyDeactivateConfirm || diyDeactivating}
                            onClick={async () => {
                              if (!data || !diyDeactivateConfirm) return;
                              setDiyDeactivating(true);
                              try {
                                const res = await apiPost('/segment/deactivate-all', {
                                  clientId: data.clientId,
                                  acknowledgedFinalInvoice: true,
                                  acknowledgementText: 'I confirm I have removed IntentFlo Audiences from my ad account(s) and understand I may still receive a final invoice for usage in the current billing period.',
                                  userId:    data.clientId,
                                  requestSource: 'web-app',
                                });
                                const r = res as { logId: string; deactivatedAt: string; billingPeriodEnd?: string };
                                setDiyDeactivateDone({ logId: r.logId, deactivatedAt: r.deactivatedAt, billingPeriodEnd: r.billingPeriodEnd });
                              } catch (e: any) {
                                alert('Failed: ' + e.message);
                              } finally {
                                setDiyDeactivating(false);
                              }
                            }}
                            style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: (!diyDeactivateConfirm || diyDeactivating) ? 'not-allowed' : 'pointer', opacity: (!diyDeactivateConfirm || diyDeactivating) ? 0.5 : 1 }}
                          >
                            {diyDeactivating ? 'Deactivating…' : 'Confirm Deactivation'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

const PLATFORM_ACCESS_STEPS = [
  { key: 'meta',    icon: '🟦', platform: 'Meta (Facebook / Instagram)', id: '908181335430042',       label: 'Partner Business ID',        instruction: "Click the button → you'll land on Meta Business Manager. Click \"Add a partner\" and paste our ID above.", url: 'https://business.facebook.com/latest/settings/partners/add' },
  { key: 'google',  icon: '🔵', platform: 'Google Ads',                  id: '665-481-1776',           label: 'Manager Account (MCC) ID',    instruction: "Click the button → go to Google Ads. Click \"Link a manager account\" and paste our ID above.",            url: 'https://ads.google.com/aw/accountaccess/managers'           },
  { key: 'linkedin',icon: '🔷', platform: 'LinkedIn Ads',                id: '7449190895490445312',    label: 'Business Manager ID',         instruction: 'Click the button → go to your Campaign Manager. Click Account Settings → Manage Access → Add user, and paste our ID above.', url: 'https://www.linkedin.com/campaignmanager/accounts'          },
  { key: 'tiktok',  icon: '⬛', platform: 'TikTok Ads',                  id: '7627972506850279441',    label: 'Business Center ID',          instruction: 'Click the button → go to TikTok Business Center. Click Partners → Add partner and paste our ID above.',   url: 'https://business.tiktok.com/manage/partner-management/invitation' },
  { key: 'twitter', icon: '✖️', platform: 'X (Twitter) Ads',             id: '18ce55vcwpp',            label: 'X Ads User ID',               instruction: 'Click the button → go to X Ads. Click Settings → Access & Security → Add user and paste our ID above.',    url: 'https://ads.x.com/settings/security'                       },
];
